import { apiServer, apiServer_v2 } from "common/config";
import { UserBadgesData, NotificationsResponse } from "../types";
import { fetchWithToken } from "./fetchWithToken";
import { resolveGuardrail, throwIfGuardrailRejected } from "./photoGuardrail";

export type FlagUserInput = {
  accessToken: string;
  userId: string;
  image: string;
  notes: string;
};
export async function flagUser({
  accessToken,
  userId,
  image,
  notes,
}: FlagUserInput) {
  const formData = new FormData();
  formData.append("user_id", userId);
  formData.append("notes", notes);

  // Only append the image part when one was actually picked. Appending a file
  // with an empty `uri` makes React Native's native multipart builder
  // (RCTHTTPFormDataHelper) crash with an NSRangeException (substringToIndex on
  // a length-0 string), which hard-crashes the app on the no-photo flag path.
  if (image) {
    const file = {
      uri: image,
      type: "image/jpeg",
      name: "image",
    };
    formData.append(`image`, file as any, "image");
  }

  const response = await fetchWithToken(apiServer + "/user/report-issue", {
    method: "POST",
    body: formData,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
      "Content-Type": "multipart/form-data",
    },
  });
  return await response?.json();
}

export async function blockUser({
  accessToken,
  userId,
  notes,
}: {
  accessToken: string;
  userId: string;
  notes: string;
}) {
  const formData = new FormData();
  formData.append("user_id", userId);
  formData.append("notes", notes);

  const response = await fetchWithToken(apiServer + "/user/block-user", {
    method: "POST",
    body: formData,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return await response?.json();
}

export async function fetchBadges(accessToken: string): Promise<{
  status: string;
  message: string;
  data: UserBadgesData;
}> {
  const response = await fetchWithToken(apiServer + "/user/sync-badges", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return await response?.json();
}

export async function fetchPublicUserBadges(userId: string): Promise<{
  status: string;
  message: string;
  data: UserBadgesData;
}> {
  const response = await fetch(apiServer + `/user/public-badges/${userId}`);
  return await response.json();
}

export async function postBasicInfo({
  name,
  gender,
  yearOfBirth,
  accessToken,
}: {
  name: string;
  gender: string;
  yearOfBirth: string;
  accessToken: string;
}) {
  const formData = new FormData();
  formData.append("name", name);
  formData.append("gender", gender);
  formData.append("year_of_birth", yearOfBirth);

  const response = await fetchWithToken(
    apiServer + "/user/profile-user-basic",
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );
  return await response?.json();
}

export async function postBasicInfoBusiness({
  name,
  jobTitle,
  jobFunction,
  jobDescription,
  accessToken,
}: {
  name: string;
  jobTitle: string;
  jobFunction: string;
  jobDescription: string;
  accessToken: string;
}) {
  const formData = new FormData();
  formData.append("name", name);
  formData.append("job_title", jobTitle);
  formData.append("job_function", jobFunction);
  formData.append("job_description", jobDescription);

  const response = await fetchWithToken(
    apiServer + "/user/profile-user-basic",
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
      },
      body: formData,
    },
  );
  return await response?.json();
}

export async function postPrivacySettings({
  key,
  value,
  accessToken,
}: {
  key: string;
  value: string;
  accessToken: string;
}) {
  const formData = new FormData();
  formData.append("pref_key", key);
  formData.append("pref_value", value);

  const response = await fetchWithToken(
    apiServer + "/user/update-user-account-preference",
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );
  return await response?.json();
}

export async function updatProfilePhoto(photo: string, accessToken: string) {
  const formData = new FormData();
  const fileName = `profile_photo.jpg`;
  const file = {
    uri: photo,
    type: "image/jpeg",
    name: fileName,
  };
  formData.append(`photo`, file as any, fileName);

  const response = await fetchWithToken(apiServer + "/user/profile-photo", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });
  const data = await response?.json();

  throwIfGuardrailRejected(response, data, ["photo"]);

  return data;
}

export async function updateProfilePhotoDesktop(
  file: File,
  accessToken: string,
) {
  const formData = new FormData();
  formData.append("photo", file, file.name); // Use the file directly

  const response = await fetchWithToken(apiServer + "/user/profile-photo", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });
  return resolveGuardrail(response, ["photo"]);
}

export async function verifyUserEmail(accessToken: string, email: string) {
  const formData = new FormData();
  formData.append("email", email);
  formData.append("token", accessToken);
  // console.log("formData", formData);

  const response = await fetch(apiServer_v2 + "/user/verify-user-email", {
    method: "POST",
    body: formData,
  });
  if (response.status === 403) {
    throw new Error("Try again after few minutes");
  }
  const data = await response.json();
  if (data.status === "error") {
    throw new Error(data.message);
  }
  return data;
}

export async function resendVerifyUserEmail(email: string, token: string) {
  const formData = new FormData();
  formData.append("email", email);
  formData.append("token", token);
  // console.log("formData", formData);

  const response = await fetch(
    apiServer_v2 + "/user/resend-verify-user-email/",
    {
      method: "POST",
      body: formData,
    },
  );
  if (response.status === 403) {
    throw new Error("Try again after few minutes");
  }
  const data = await response.json();
  if (data.status === "error") {
    throw new Error(data.message);
  }
  return data;
}

export async function fetchNotifications(
  accessToken: string,
  page: number = 1,
  per_page: number = 20,
  notification_type: "live_alert" | "accessibility_information" | "all" = "all",
  unread_only: "yes" | "no" = "no",
  search?: string,
): Promise<NotificationsResponse> {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString(),
    });

    if (notification_type !== "all") {
      queryParams.append("notification_type", notification_type);
    }

    if (unread_only === "yes") {
      queryParams.append("unread_only", unread_only);
    }

    if (search && search.trim()) {
      queryParams.append("search", search.trim());
    }

    const response = await fetchWithToken(
      `${apiServer}/user/notifications?${queryParams.toString()}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    const data = await response?.json();

    // Check if the response has the expected structure
    if (data?.status === "success" && data?.data) {
      return {
        pagination: data.data.pagination || {
          current_page: page,
          last_page: 1,
          per_page: per_page,
          total: 0,
          from: 0,
          to: 0,
        },
        notifications: data.data.notifications || [],
      };
    }

    if (data?.status === "error") {
      throw new Error(data.message || "Failed to fetch notifications");
    }

    // Return empty data with default pagination if request failed
    return {
      pagination: {
        current_page: page,
        last_page: 1,
        per_page: per_page,
        total: 0,
        from: 0,
        to: 0,
      },
      notifications: [],
    };
  } catch (error: any) {
    console.error("Error fetching notifications:", error);
    // Return empty data with default pagination on error
    return {
      pagination: {
        current_page: page,
        last_page: 1,
        per_page: per_page,
        total: 0,
        from: 0,
        to: 0,
      },
      notifications: [],
    };
  }
}

export async function markAllNotificationsAsRead(accessToken: string): Promise<{
  status: string;
  message: string;
  data: any[];
}> {
  const response = await fetchWithToken(
    apiServer + "/user/notifications/read-all",
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
  return await response?.json();
}

export async function markNotificationAsRead(
  accessToken: string,
  notificationId: string,
): Promise<{
  status: string;
  message: string;
  data: any[];
}> {
  const response = await fetchWithToken(
    apiServer + `/user/notifications/${notificationId}/read`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
  return await response?.json();
}
